
import React, { memo, useMemo } from "react";
import { Button } from "@/components/ui/button";
import ArticleCard2 from "./ArticleCard2";
import { Article } from "@/data/articlesData";

interface ArticleGrid2Props {
  articles: Article[];
  visibleCount: number;
  onLoadMore: () => void;
  skipFirst?: boolean;
  partnerName?: string;
  featuredArticleId?: string;
  title?: string;
}

const ArticleGrid2: React.FC<ArticleGrid2Props> = memo(({
  articles,
  visibleCount,
  onLoadMore,
  skipFirst = false,
  partnerName,
  featuredArticleId,
  title = "Artikkelit"
}) => {
  // Memoize filtered articles for better performance
  const displayArticles = useMemo(() => {
    return articles
      .filter(article => !featuredArticleId || article.id !== featuredArticleId)
      .slice(skipFirst ? 1 : 0, visibleCount);
  }, [articles, featuredArticleId, skipFirst, visibleCount]);

  const hasMoreArticles = useMemo(() => {
    return articles.length > visibleCount + (skipFirst ? 1 : 0);
  }, [articles.length, visibleCount, skipFirst]);
  
  // Performance optimization: prioritize first 6 cards for faster initial paint
  const priorityArticles = useMemo(() => displayArticles.slice(0, 6), [displayArticles]);
  const deferredArticles = useMemo(() => displayArticles.slice(6), [displayArticles]);

  if (displayArticles.length === 0) {
    return (
      <div className="text-center py-12" id="article-grid">
        <div className="max-w-md mx-auto">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Ei tuloksia</h3>
          <p className="text-gray-600">Kokeile eri hakusanoja tai valitse toinen kategoria.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-12" id="article-grid">
      {title && (
        <div className="mb-8">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">{title}</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full"></div>
        </div>
      )}

      {/* Article grid with improved responsive design */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
        {/* Priority articles (first 6) with priority loading */}
        {priorityArticles.map((article, index) => (
          <ArticleCard2 
            key={article.id} 
            article={article} 
            showPartnerLogo={article.category.toLowerCase() === "yrityslainat"} 
            partnerName={partnerName}
            priority={index < 3} // First 3 cards get priority loading
          />
        ))}
        
        {/* Deferred articles for better performance */}
        {deferredArticles.map(article => (
          <ArticleCard2 
            key={article.id} 
            article={article} 
            showPartnerLogo={article.category.toLowerCase() === "yrityslainat"} 
            partnerName={partnerName}
            priority={false}
          />
        ))}
      </div>

      {/* Load more button with improved styling */}
      {hasMoreArticles && (
        <div className="flex justify-center mt-12">
          <Button 
            variant="outline" 
            onClick={onLoadMore} 
            size="lg"
            className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white font-semibold px-8 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
          >
            Näytä lisää artikkeleita
            <span className="ml-2">({articles.length - visibleCount} jäljellä)</span>
          </Button>
        </div>
      )}
    </div>
  );
});

ArticleGrid2.displayName = "ArticleGrid2";

export default ArticleGrid2;
